// ERP Billing Service javoblari bilan ishlash uchun yordamchi funksiyalar.
//
// Nega kerak: swagger (/billing/swagger/doc.json) javoblarni "yalang'och" tanada
// (massiv / obyekt / integer) hujjatlashtirgan, lekin gateway ularni {data: ...}
// ichiga o'raydi — erp-optom-seller-frontend ayni shu host'da billing javoblarini
// `response.data.data` deb o'qiydi va uning axios'ida unwrap qiluvchi interceptor yo'q.
// Shuning uchun ikkala shaklni ham qo'llab-quvvatlaymiz: envelope bo'lsa ichini,
// bo'lmasa tananing o'zini qaytaramiz.

/**
 * Javob haqiqiy axios javobi ekanini tekshiradi, aks holda xato tashlaydi.
 *
 * Nega kerak: src/pages/main/MainLayout.js dagi global 401 interceptor rad etishni
 * `Promise.resolve({handled: true})` ga aylantiradi. Bunday "javob"da na status,
 * na data bo'ladi — uni muvaffaqiyat deb qabul qilsak, hech narsa saqlanmagan holda
 * foydalanuvchiga yashil toast ko'rsatiladi va modal yopiladi. Shuning uchun buni
 * xatoga aylantiramiz: chaqiruvchi .catch() ga tushadi va xato toast'i chiqadi.
 *
 * @param {object} response axios javobi
 * @returns {object} o'sha javobning o'zi
 */
export const assertBillingResponse = (response) => {
    if (!response || typeof response.status !== 'number') {
        const error = new Error('Billing request was not completed: session expired or the request was intercepted');
        error.isBillingInterrupted = true;
        throw error;
    }

    return response;
};

/**
 * Axios javobidan foydali yukni ajratib oladi ({data: X} envelope'ini yechadi).
 *
 * @param {object} response axios javobi
 * @returns {*} envelope ichidagi qiymat yoki tananing o'zi
 */
export const unwrapBillingData = (response) => {
    assertBillingResponse(response);

    const body = response.data;

    if (body !== null && typeof body === 'object' && !Array.isArray(body) && 'data' in body) {
        return body.data;
    }

    return body;
};

/**
 * Ro'yxat qaytaradigan endpointlar uchun: har doim massiv qaytaradi.
 * Go bo'sh slice'ni `null` deb serializatsiya qilishi mumkin.
 *
 * @param {object} response axios javobi
 * @returns {Array}
 */
export const unwrapBillingList = (response) => {
    const data = unwrapBillingData(response);
    return Array.isArray(data) ? data : [];
};

/**
 * /count endpointlari uchun: har doim son qaytaradi.
 *
 * @param {object} response axios javobi
 * @returns {number}
 */
export const unwrapBillingCount = (response) => {
    const data = unwrapBillingData(response);
    const count = Number(data);
    return Number.isFinite(count) ? count : 0;
};

/**
 * Billing xatosidan foydalanuvchiga ko'rsatish mumkin bo'lgan matnni ajratadi.
 *
 * Qo'llab-quvvatlanadigan shakllar:
 *   - {error: {code: 2000, message: "..."}}  — gateway envelope'i
 *   - {error: "..."}                          — oddiy matn
 *   - {message: "..."}
 *   - {"field": "xabar", ...}                 — swagger'dagi map<string,string>
 *   - "matn"                                  — text/plain
 *
 * @param {object} error axios xatosi
 * @returns {string|null} xabar topilmasa null (chaqiruvchi umumiy toast ko'rsatadi)
 */
export const getBillingErrorMessage = (error) => {
    const data = error?.response?.data;

    if (!data) return null;

    if (typeof data === 'string') {
        const text = data.trim();
        return text ? text : null;
    }

    if (typeof data !== 'object') return null;

    const inner = data.error;

    if (typeof inner === 'string' && inner.trim()) return inner.trim();

    if (inner && typeof inner === 'object') {
        if (typeof inner.message === 'string' && inner.message.trim()) return inner.message.trim();
        if (inner.code) return `#${inner.code}`;
    }

    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();

    const messages = Object.values(data).filter(value => typeof value === 'string' && value.trim());

    return messages.length ? messages.join(', ') : null;
};

/**
 * Xatoning HTTP statusi (masalan 409 — chegirma allaqachon mavjud, 422 — summa cheklovi).
 *
 * @param {object} error axios xatosi
 * @returns {number|null}
 */
export const getBillingErrorStatus = (error) => error?.response?.status ?? null;
